import { useEffect } from 'react';

/**
 * Recursively walks down the DOM tree to find the active element within any
 * shadow DOM that it might be contained in.
 */
function findActiveElementWithinShadow(element: Element | null = document.activeElement): Element | null {
  if (element?.shadowRoot) {
    return findActiveElementWithinShadow(element.shadowRoot.activeElement);
  }
  return element;
}

/**
 * We need to hack the window.getSelection method to use the shadow DOM,
 * since the mobiledoc editor internals need to get the selection to detect
 * cursor changes. First, we walk down into the shadow DOM to find the
 * actual focused element. Then, we get the root node of the active element
 * (either the shadow root or the document itself) and call that root's
 * getSelection method.
 */
function patchGetSelection() {
  const oldGetSelection = window.getSelection.bind(window);
  window.getSelection = (useOld: boolean = false) => {
    const activeElement = findActiveElementWithinShadow();

    const shadowRootOrDocument: ShadowRoot | Document = activeElement
      ? (activeElement.getRootNode() as ShadowRoot | Document)
      : document;

    // Firefox does not have getSelection() on the shadow root, so we need to fall back to the document in that case.
    const selection = (shadowRootOrDocument as any)?.getSelection?.() ?? document.getSelection();

    if (!selection || useOld) {
      return oldGetSelection();
    }
    return selection;
  };
}

let patched = false;

export function PolyfillShadowDomSelectionPlugin() {
  useEffect(() => {
    if (patched) return;

    patchGetSelection();
    patched = true;
  }, []);

  return null;
}
